import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { HistoryGlyph } from "../labInvestigations/labIcons";
import ActivityService from "./ActivityService";
import DeleteConfirmDialog from "../common/DeleteConfirmDialog";
import DeletedEntriesModal from "./DeletedEntriesModal";
import TrendModal from "./TrendModal";
import { activityStatusBand, activityIntensityBand } from "./data";
import MetaTooltip from "../medicines/MetaTooltip";
import { ACT_V2_ADD, ACT_V2_EDIT, ACT_V2_EDIT_CAPTURED, ACT_V2_STATUS, ACT_V2_REFRESH } from "./events";

/* Board 3408:2734 — the Material `delete` glyph, FILLED, 16px, #e44437.
   The section had a 20-box stroked outline borrowed from Family history. */
const TrashIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M4.667 14c-.367 0-.681-.13-.942-.392a1.284 1.284 0 0 1-.392-.941V4h-.666V2.667h3.333V2h4v.667h3.333V4h-.666v8.667c0 .366-.131.68-.392.941-.261.261-.575.392-.942.392H4.667Zm6.666-10H4.667v8.667h6.666V4ZM6 11.333h1.333v-6H6v6Zm2.667 0H10v-6H8.667v6Z" />
  </svg>
);

/* Board I3071:4188;629:44 — a 1.4-stroke plus in a 12.4 box, #26284d.
   Rendered at 11px, per the instance's own size. */
const PlusIcon = () => (
  <svg viewBox="0 0 12.4 12.4" width="11" height="11" fill="none" aria-hidden="true">
    <path
      d="M6.2 0.7V11.7M0.7 6.2H11.7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Board I3071:4208;645:1713 — the chip's caret is a 16px STROKED chevron
   inheriting the band's own ink, not an 8px U+25BE glyph. */
const ChipCaret = () => (
  <svg viewBox="0 0 11.33 6.67" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M10.3333 1L5.66667 5.66667L1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Physical activity — API-backed activity log. Row click → Edit modal.
 * Status chip → status-only Update modal. Per-row Delete button. No
 * secondary cards on this section right now (Activity-tracking and Apple
 * Health cards were removed pending their own v2 backends).
 */

/* Scoped to pa3-*: .tx-chip is shared with Medicines and Addictions and must
   keep its hug-content pill box. The board draws two DIFFERENT chips — the
   INTENSITY one is 108x22 with a caret, the STATUS one is 92 wide with none —
   so `wide` selects between them rather than one box serving both. */
const BandChip = ({ band, onClick, disabled, caret, kind }) => {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      {...(onClick ? { type: "button", onClick, disabled, title: "Update status" } : {})}
      className={`pa3-chip pa3-chip-${kind}`}
      style={{ background: band.bg, color: band.fg }}
    >
      <span className="pa3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="pa3-chip-label">{band.label}</span>
      {caret && <ChipCaret />}
    </Tag>
  );
};
BandChip.propTypes = {
  band: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  caret: PropTypes.bool,
  /** "intensity" (the board's 108x22 box) or "status" (its 92 box). */
  kind: PropTypes.oneOf(["intensity", "status"]).isRequired,
};

/* Board 3976:15082 — the empty state's medallion glyph: a clipboard with a
   rule and a tick, Material `assessment` family. */
/* Board 3976:15082 `icon/section/assessment` — a 26px clipboard-with-check
   STROKED at 1.5 with round caps/joins: body rect, top tab, tick. It was a
   FILLED Material clipboard-with-bar-chart before, which is a different glyph
   in a different treatment. Ink inherits from the medallion (#6b7280). */
const AssessmentIcon = () => (
  <svg viewBox="0 0 26 26" width="26" height="26" fill="none" aria-hidden="true">
    <path
      d="M5.85 5.2H20.15V22.1H5.85V5.2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.75 3.25H16.25V7.15H9.75V3.25Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.75 14.56L11.83 16.64L16.25 12.35"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Board 3071:4555 `icon/plus` — the CTA's own 16px stroke-2 plus with arms
   inset to 3.33–12.67. The toolbar's PlusIcon is an 11px 1.4-stroke glyph
   from the hero board; scaling it up to 16 thickens the stroke to ~1.8 and
   runs the arms nearly full-bleed, so the CTA gets the board's own vector. */
const CtaPlusIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M3.33333 8H12.6667M8 3.33333V12.6667"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Build a synthetic Activity-shaped row from a staged "add" captured
// item so it can slot into the same table as Activity v2 rows. Mirrors
// the other v2 sections. Wave B first-cut: staged UPDATES aren't
// rendered.
const stagedAddToActivityRow = (item) => {
  const p = (item && item.payload) || {};
  return {
    activityId:        `staged-${item.id}`,
    name:              p.name || p.value || "(untitled)",
    category:          p.category || "",
    daysPerWeek:       Number(p.days_per_week) || Number(p.daysPerWeek) || 0,
    minutesPerSession: Number(p.minutes_per_session) || Number(p.minutesPerSession) || 0,
    intensity:         p.intensity || "moderate",
    status:            p.status || "active",
    description:       p.description || "",
    _captured:         item,
    _capturedIsAi:     (item.confidence || 0) > 0,
    _stagedAdd:        true,
  };
};

const PhysicalActivity = ({ patient, capturedItems, onRowsSync }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // activity awaiting confirm
  const [trendTarget, setTrendTarget] = useState(null);   // activity → trend modal
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  // The rail owns the sheet (and its de-duplication), so this only announces
  // the intent. `rows` rides along because the form's duplicate check runs
  // against the current table on every keystroke.
  const openAdd = useCallback(() => {
    EventService.emit(ACT_V2_ADD, { existingList: rowsRef.current });
  }, []);
  // Transcript-mode captured rows route to a re-edit event so the modal
  // can pop the AI proposal back open as an editable card rather than
  // opening the standalone edit sheet in the patient-page rail.
  const openEdit = useCallback((activity) => {
    if (activity && activity._captured) {
      EventService.emit(ACT_V2_EDIT_CAPTURED, {
        captured: activity._captured,
        actv2Record: activity._stagedAdd ? null : activity,
      });
      return;
    }
    EventService.emit(ACT_V2_EDIT, { activity, existingList: rowsRef.current });
  }, []);
  // Board 3071:4666 — the status-only Update sheet lives in the same rail;
  // the `mode` marker is what routes it to ActivityStatusForm there. Same
  // captured-row branch as openEdit above so the reviewer's click reaches
  // the AI proposal card instead of leaking to the standalone rail.
  const openStatus = useCallback((activity) => {
    if (activity && activity._captured) {
      EventService.emit(ACT_V2_EDIT_CAPTURED, {
        captured: activity._captured,
        actv2Record: activity._stagedAdd ? null : activity,
      });
      return;
    }
    EventService.emit(ACT_V2_STATUS, { activity, mode: "status" });
  }, []);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new ActivityService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { activities: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load activities");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(ACT_V2_REFRESH, load);
    return () => EventService.removeListener(ACT_V2_REFRESH, load);
  }, [load]);

  const rowsRaw = useMemo(() => (doc && doc.activities) || [], [doc]);

  // Bubble the current base v2 list up to the transcript modal so it
  // can dedup AI proposals against the patient's existing rows
  // (proposals whose name matches an existing entry render as an
  // update card on that record instead of a duplicate add card).
  useEffect(() => {
    if (typeof onRowsSync === "function") onRowsSync(rowsRaw);
  }, [rowsRaw, onRowsSync]);

  // Transcript-mode overlay. Adds slot in as new peach rows; updates
  // annotate the matching existing activity with _captured so the
  // reviewer sees the peach highlight on the row they just modified.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const rows = useMemo(() => {
    if (!inTranscriptMode) return rowsRaw;
    const stagedAdds = [];
    const updatesByEntryId = new Map();
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if (it.operation === "add") stagedAdds.push(stagedAddToActivityRow(it));
      else if (it.operation === "update" && it.targetEntryId) {
        updatesByEntryId.set(String(it.targetEntryId), it);
      }
    }
    // Overlay staged payload fields onto the base row so the table
    // columns (Days per week / Minutes / Intensity / Status /
    // Description) reflect what the reviewer just saved — not the
    // untouched Activity v2 values. Mirrors CC's withEffective /
    // Dx's overlay pattern.
    const annotated = rowsRaw.map((row) => {
      const update = updatesByEntryId.get(String(row.activityId));
      if (!update) return row;
      const p = (update.payload) || {};
      return {
        ...row,
        name:              p.name        || row.name,
        category:          p.category    || row.category,
        daysPerWeek:       p.days_per_week      != null ? p.days_per_week      : row.daysPerWeek,
        minutesPerSession: p.minutes_per_session != null ? p.minutes_per_session : row.minutesPerSession,
        intensity:         p.intensity   || row.intensity,
        status:            p.status      || row.status,
        description:       p.description !== undefined ? p.description : row.description,
        _captured:         update,
        _capturedIsAi:     (update.confidence || 0) > 0,
      };
    });
    return [...annotated, ...stagedAdds];
  }, [rowsRaw, capturedItems, inTranscriptMode]);
  // Read by the two emitters above without making them change identity on
  // every refetch.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  // Soft-deleted activities ride along on the same GET. Toolbar button
  // shows only when there is at least one deleted row.
  const deletedRows = useMemo(
    () => (doc && Array.isArray(doc.deletedActivities) ? doc.deletedActivities : []),
    [doc]
  );

  // Second-step delete — after the shared confirm dialog is approved. The
  // dialog itself just captures the target; the network call fires here.
  const confirmDelete = () => {
    const a = deleteTarget;
    if (!a) return;
    setDeletingId(a.activityId);
    const svc = new ActivityService();
    svc.delete(
      patientId,
      a.activityId,
      (doc && doc.version) || 0,
      (res) => {
        setDoc(res || doc);
        setDeletingId(null);
        setDeleteTarget(null);
        EventService.emit(ACT_V2_REFRESH);
      },
      (err) => {
        setDeletingId(null);
        setDeleteTarget(null);
        if (err && err.reason === "version") load();
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Delete failed — please retry");
      }
    );
  };

  // Mirrors the render chain below: the branches ABOVE the empty state
  // (no patient / first load / error) must not count as "empty".
  const isEmpty =
    !!patientId && !error && !(loading && !doc) && rows.length === 0;

  return (
    <div className="pa3-card">
      <header className="pa3-toolbar">
        <h2 className="pa3-title">Physical activity</h2>
        {/* Board 3071:4186 — the middot is part of the string; the toolbar's
            9px gap supplies the space before it. */}
        <span className="pa3-count">
          &middot; {rows.length === 0 ? "No entries" : `${rows.length} entries`}
        </span>
        <div className="pa3-actions">
          {deletedRows.length > 0 && (
            <button
              type="button"
              className="pa3-btn"
              onClick={() => setShowDeletedModal(true)}
              title="View deleted activities"
            >
              <HistoryGlyph /> Deleted entries
              <span className="pa3-btn-count"> &middot; {deletedRows.length}</span>
            </button>
          )}
          {/* Board 3071:4525 — the EMPTY state's toolbar carries no Add
              button; the body CTA (3071:4554) is the sole add path, so a
              toolbar twin would be a duplicate affordance. Deleted-entries
              stays whenever deleted rows exist: the board's sample simply
              has none, and hiding it would strand real data. */}
          {!isEmpty && (
            <button
              type="button"
              className="pa3-btn pa3-btn-add"
              onClick={openAdd}
              disabled={!patientId}
            >
              <PlusIcon /> Add activity
            </button>
          )}
        </div>
      </header>

      {!patientId ? (
        <div className="ph-slot-empty"><p>Waiting for patient…</p></div>
      ) : loading && !doc ? (
        <div className="ph-slot-empty"><p>Loading activities…</p></div>
      ) : error ? (
        <div className="ph-slot-empty">
          <p>{error}</p>
          <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        /* Board 3071:4541 — the empty state keeps the toolbar AND the header
           row; only the body is replaced. The header here is the board's OWN
           FIVE-column row (3071:4530: 150/150/160/160/160), not the filled
           table's eight — it drops Description / Analysis / actions and
           re-spreads the tracks. */
        <>
          <div className="pa3-table" role="table">
            <div className="pa3-grid">
              <div className="pa3-thead pa3-thead-empty" role="row">
                <span role="columnheader">Activity</span>
                <span role="columnheader">Days per week</span>
                <span role="columnheader">Minutes per session</span>
                <span role="columnheader">Intensity</span>
                <span role="columnheader">Status</span>
              </div>
            </div>
          </div>
          <div className="pa3-empty">
            <span className="pa3-empty-medallion" aria-hidden="true">
              <AssessmentIcon />
            </span>
            <p className="pa3-empty-title">No physical activity recorded</p>
            <p className="pa3-empty-hint">
              Nothing has been logged yet. Add the patient&rsquo;s first activity to
              begin building their history.
            </p>
            <button type="button" className="pa3-empty-cta" onClick={openAdd}>
              <CtaPlusIcon /> Add activity
            </button>
          </div>
        </>
      ) : (
        <div className="pa3-table" role="table">
          <div className="pa3-grid">
            <div className="pa3-thead" role="row">
              <span role="columnheader">Activity</span>
              <span role="columnheader">Days per week</span>
              <span role="columnheader">Minutes per session</span>
              <span role="columnheader">Intensity</span>
              <span role="columnheader">Description</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Analysis</span>
              <span role="columnheader" aria-label="Actions" />
            </div>

            {rows.map((a) => (
              <div
                className={`pa3-row${a._captured ? " is-captured" : ""}`}
                role="row"
                key={a.activityId}
              >
                <span className="pa3-cell pa3-c-name" role="cell">
                  {/* Captured (peach) rows stay clickable — openEdit
                      branches on _captured and routes to
                      ACT_V2_EDIT_CAPTURED so the modal can pop the AI
                      proposal back open as an editable card. The old
                      is-static span made the row inert on reopen. */}
                  <button
                    type="button"
                    className="pa3-name-btn"
                    onClick={() => openEdit(a)}
                    title={a._stagedAdd ? "Edit captured activity" : "Edit activity"}
                  >
                    {a._capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                    {a.name}
                  </button>
                  {a.category && <span className="pa3-cat"> &middot; {a.category}</span>}
                </span>
                <span className="pa3-cell pa3-c-days" role="cell">{a.daysPerWeek}</span>
                <span className="pa3-cell pa3-c-mins" role="cell">{a.minutesPerSession}</span>
                <span className="pa3-cell pa3-c-intensity" role="cell">
                  <BandChip band={activityIntensityBand(a.intensity)} kind="intensity" />
                </span>
                <span className="pa3-cell pa3-c-desc" role="cell">
                  {/* 3-line clamp + white hover tooltip; only reveals
                      when the text is actually being truncated. Same
                      pattern Family history uses. */}
                  <MetaTooltip text={a.description || ""} lines={3} />
                </span>
                <span className="pa3-cell pa3-c-status" role="cell">
                  {/* Captured rows keep the chip clickable — routes
                      through openStatus, which branches on _captured
                      and emits ACT_V2_EDIT_CAPTURED to the modal. */}
                  <BandChip
                    band={activityStatusBand(a.status)}
                    onClick={() => openStatus(a)}
                    kind="status"
                  />
                </span>
                <span className="pa3-cell pa3-c-analysis" role="cell">
                  {a._stagedAdd ? (
                    <span className="pa3-dash">&mdash;</span>
                  ) : (
                    <button
                      type="button"
                      className="pa3-trend-link"
                      onClick={() => setTrendTarget(a)}
                      title="View audit history"
                    >
                      View trend
                    </button>
                  )}
                </span>
                <span className="pa3-cell pa3-c-actions" role="cell">
                  {!a._stagedAdd && (
                    <button
                      type="button"
                      className="pa3-del"
                      onClick={() => setDeleteTarget(a)}
                      disabled={deletingId === a.activityId}
                      aria-label={`Delete ${a.name}`}
                      title="Delete"
                    >
                      {deletingId === a.activityId ? "…" : <TrashIcon />}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          name={deleteTarget.name}
          listLabel="activity"
          submitting={deletingId === deleteTarget.activityId}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {showDeletedModal && (
        <DeletedEntriesModal
          patient={patient}
          entries={deletedRows}
          onClose={() => setShowDeletedModal(false)}
        />
      )}

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          activityId={trendTarget.activityId}
          name={trendTarget.name}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

PhysicalActivity.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
  /** Fires whenever the base v2 activity list changes. Transcript
   *  modal uses it to dedup AI proposals against existing rows. */
  onRowsSync: PropTypes.func,
};

export default PhysicalActivity;
